import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter, Redirect } from 'react-router-dom';
import styled from 'styled-components';
import { Frame } from 'framer';
import mongoose from 'mongoose';
import socketIOClient from "socket.io-client";
import { toast as setToast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import { createBrowserHistory } from 'history';
import _ from 'lodash';

import { BetaModal, ToastBlock, ItemModal, UpdatesModal } from 'features'

import { pageContext, clientContext } from 'context';
import { socketUpdates, getPlainText, getCookie } from 'utils';
import { Item, Client } from 'schema';
import * as actionCreators from 'store/actions/index'

const Container = styled.div`
    width: 100vw;
    height: 100vh;
    margin: 0;
    background-color: var(--primary-color);
    z-index: 100;
    position: absolute;
    top: 0;
    left: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
`

const Wrapper = styled.div`
    display: inline-flex;
    flex-direction: row;
`

const Title = styled.div`
    position: absolute;
    font-size: 2rem;
    font-weight: bolder;
    text-shadow: -2px 2px 2px var(--primary-color);
`

const Subtitle = styled.div`
    position: absolute;
    font-size: 1rem;
    font-weight: bolder;
    transform: translateY(2rem);
    text-shadow: -2px 2px 2px var(--primary-color);
`

const LoadStyle = {
    width: '2rem',
    height: '10rem',
    backgroundColor: 'var(--secondary-color)',
    borderRadius: '5rem',
    margin: '1rem',
    boxShadow: '-7px 7px 7px #000'
}

const transition = {
    flip: Infinity,
    ease: "linear",
    duration: 0.5,
}

const LoadingScreen = props => {
    const {
        pathName,
        message
    } = props;
    const debug = false
    let history = createBrowserHistory();
    const dispatch = useDispatch()
    const [redirect, setRedirect] = React.useState(null)
    const user = useSelector(state => state.auth.user)
    const data = useSelector(state => state.db.present.data && state.db.present.data.toJS())
    const clients = useSelector(state => state.integration.clients)
    const workflows = useSelector(state => state.integration.workflows)
    const sessionId = useSelector(state => state.content.sessionId)
    const [start_time, setStart] = React.useState(performance.now())

    const path = pathName || '/'
    const isRedirect = path.split('/').slice(1)[0] === 'redirect' 
    let pageID = !user ? pathName.split('/').slice(2)[0]
                : user & isRedirect ? path
                : user ? user['activePageID'].toLowerCase()
                : null
    let itemID = pathName.split('/').slice(3)[0]
    const localID = localStorage.getItem('--atomicly-token')
    // const localID = getCookie('--atomicly-token')

    const redirectToPage = () => {
        if(user && !user['setup_completed']){
            setRedirect(<Redirect to={`/getting-started`}/>)
            debug && console.log('[LOADING] Redirect to /getting-started')
        }
        else if(Object.keys(data['pages']).length > 0){
            setRedirect(<Redirect to={isRedirect ? path : `/app/${pageID}`}/>)
            setup()
            debug && console.log(`[LOADING] Redirect to ${isRedirect ? path : `/app/${pageID}`}`)
        } else {
            debug && console.log('[LOADING] No redirect', Object.keys(data['pages']))
        }
    }

    const setupModal = () => {
        // Login modal
        if(user['show_loginModal']){
            dispatch(actionCreators.setModal(<BetaModal/>, 'BetaModal'))
        } 
        // Updates modal
        else if (user['show_updatesModal']) {
            dispatch(actionCreators.setModal(<UpdatesModal/>, 'BetaModal'))
        } 
        // Item modal
        else if (itemID && data['items'][itemID]){
            dispatch(actionCreators.setModal(<ItemModal itemID={itemID}/>, `${itemID}_ItemModal`))
        }
    }

    const setupSocket = () => {
        const disableSocket = process.env.REACT_APP_DISABLE_SOCKET;
        const disableClientToast = true
        if(disableSocket){ return }
        if(!user) { return }

        // Only listen to pages that user has access to on login
		const userId = localStorage.getItem('userid')
        // const userId = cookies['atomicly-uid']
        const updatedBy = [userId, sessionId].join('@')
        let sockets = []

        // 1. Main db sockets
        user['activePages'].forEach(pageId => {
            const socket = socketIOClient.connect(process.env.REACT_APP_SOCKET_ENDPOINT, {query: {userId: userId, sessionId: sessionId, pageId}});
            sockets.push(socket)
            socket.on('connect', function() {
                debug && console.log('[SOCKET] connect', socket.connected, userId, sessionId);
            });

            socket.on(pageId, res => {
                debug && console.log('[SOCKET] updateData', {sessionId, pageId, res})
                debug && console.log('[SOCKET] updateData', {current: updatedBy, socket: res['updatedBy'], res})
                if((res['updatedBy'] !== updatedBy) || (res['operationType'] === 'delete')){
                    // 1.1 Update data
                    debug && console.log('[SOCKET] Should update')
                    let body = {...res}
                    dispatch(actionCreators.updateSocketData(res))
                    // const updatedData = socketUpdates(body)
                    // dispatch(actionCreators.updateData(updatedData))

                    // 1.2 Trigger toast if new item
                    const operationType = res['operationType']
                    if(!disableClientToast && operationType === 'insert' && res['updatedBy'] === 'ATOMICLY'){
                        const item = new Item(res.fullDocument)
                        const options = { containerId: 'first', toastId: item['_id'] }
                        const title = getPlainText(item['content'])
                        const subtitle = `From ${clientContext[item['client']]['title']}(${item.clientTitle['account']}) - ${item.clientTitle['source']}`
                        setToast(<ToastBlock title={title} subtitle={subtitle}
                                    onSelect={() => item && dispatch(actionCreators.setModal(<ItemModal itemID={item['itemID']}/>, `${item['_id']}_ItemModal`))}
                                    />, options)
                    }

                }
            })
        })
		
        // 2. Client socket
        const clientSocket = socketIOClient.connect(process.env.REACT_APP_SOCKET_ENDPOINT, {query: {userId: userId, client: true}});
        sockets.push(clientSocket)
        clientSocket.on(`updateClient-${user['uid']}`, res => {
			debug && console.log('[SOCKET] updateClient', clients, res)
            let updatedClients = {...clients}
            const operationType = res['operationType']
            if(operationType === 'insert'){
                const newClient = new Client(res['fullDocument'])
                updatedClients[res.documentKey['_id']] = newClient
                dispatch(actionCreators.updateClients(updatedClients))
            } else if (operationType === 'update') {
                if(Object.keys(clients).includes(res.documentKey['_id'])){
                    const newClient = new Client(res['fullDocument'])
                    updatedClients[res.documentKey['_id']] = newClient
                    dispatch(actionCreators.updateClients(updatedClients))
                }
            } else if (operationType === 'delete'){
                delete updatedClients[res.documentKey['_id']]
                dispatch(actionCreators.updateClients(updatedClients))
            }
			dispatch(actionCreators.setModal(null, null))
		})

        // 3. Client workflow sockets
        // socket.on(`updateClientWorkflows-${this.props.user['uid']}`, res => {
        //     debug && console.log('[SOCKET] updateClientWorkflow', res)
        //     let updatedWorkflows = {...this.props.workflows}
        //     const operationType = res['operationType']
        //     const workflowId = res.documentKey['_id']
        //     if(operationType === 'delete'){
        //         delete updatedWorkflows[workflowId]
        //         this.props.updateWorkflows(updatedWorkflows)
        //     }
        // })

        // socket.on('deleteClientWorkflow', res => {
        //     debug && console.log('[SOCKET] deleteClientWorkflow', res)
        //     let updatedWorkflows = {...workflows}
        //     const workflowId = res['workflowId']
        //     if(Object.keys(updatedWorkflows).includes(workflowId)){
        //         delete updatedWorkflows[workflowId]
        //         dispatch(actionCreators.updateWorkflows(updatedWorkflows))
        //     }
        // })

        window.addEventListener('beforeunload', () => {
            console.log('[Disconnect socket]')
            sockets.forEach(socket => {
                socket.disconnect()
            })
        });
    }

    const setupHistory = () => {
        const unlisten = history.listen((location, action) => {
            const pathName = location['pathname']
            const pageID = pathName.split('/').slice(-1)[0]
            const isPage = mongoose.Types.ObjectId.isValid(pageID)
            const isModule = Object.keys(pageContext.MODULES).includes(pageID.toUpperCase())
            if(isPage || isModule){
                if(user && pageID !== user['activePageID']){
                    const type = isPage ? pageContext.TYPES.PAGES : pageContext.TYPES.MODULES
                    const oldUser = _.cloneDeep(user)
                    let updatedUser = {...user}
                    updatedUser['activePageID'] = type === pageContext.TYPES.MODULES ? pageID.toUpperCase() : pageID
                    updatedUser['activePageType'] = type
                    pageID !== oldUser['activePageID'] && dispatch(actionCreators.updateUser(user, oldUser))
                    window.scrollTo(0,0)
                }
                unlisten()
            }
        })
    }

    const setupNoti = () => {
        if(![true, false].includes(user['desktop_notifications']) && Notification.permission === 'default') {
            let options = {
                toastId: uuidv4(),
                containerId: 'first',
                onClick: () => Notification.requestPermission().then(result => {
                    if(result === 'granted'){
                        const oldUser = _.cloneDeep(user)
                        let updatedUser = {...user}
                        updatedUser['desktop_notifications'] = true
                        dispatch(actionCreators.updateUser(updatedUser, oldUser))
                    }

                })
            } 
            setToast.info(<ToastBlock title='Enable desktop notifications?' subtitle="To remind you about your scheduled tasks"/>, options)
        }
    }

    const setup = () => {
        setupModal()
        setupSocket()
        setupHistory()
        setupNoti()
        // console.log('[LOADING] Setup complete')
    }

    React.useEffect(() => {
        // 1. Redirect to home page
        if(!localID || localID === 'undefined'){
            process.env.REACT_APP_ENV === 'DEVELOPMENT' ? setRedirect(<Redirect to='/'/>) : window.location = 'https://atomicly.io'
            debug && console.log('CASE 1')
        }

        // 2. Log user in
        else if(localID && localID !== 'undefined'){
            if(!user){
                let page
                if(pageID){
                    const isPage = mongoose.Types.ObjectId.isValid(pageID) & pageID !== 'integrations'
                    const isModule = pageID.includes('settings') || Object.keys(pageContext.MODULES).includes(pageID.toUpperCase())
                    page = {
                        id: isPage ? pageID 
                            : isModule ? pageID.toUpperCase() : null, 
                        type: isPage ? pageContext.TYPES.PAGES 
                            : isModule ? pageContext.TYPES.MODULES : null
                    }
                }
                debug && console.log('CASE 2')
                // dispatch(actionCreators.signInExisting(localID, null ,page))
                dispatch(actionCreators.signInExisting(null ,page, redirectToPage))
            } else {
                redirectToPage()
                debug && console.log('CASE 3')
            }
        }
    }, [])

    // console.log(`[LOADING] Time taken ${((performance.now() - start_time)/1000).toFixed(2)}s`, user)

    return (
        <Container>
            {redirect}
            <Wrapper>
                <Frame 
                    position="relative"
                    style={LoadStyle} 
                    animate={{y:'10px'}} 
                    transition={transition}
                    />
                <Frame 
                    position="relative"
                    initial={{y: '10px'}}
                    style={LoadStyle} 
                    animate={{y:'0px'}} 
                    transition={transition}
                />
            </Wrapper>
            <Title>LOADING</Title>
            {message ? <Subtitle>{message}</Subtitle> : null}
        </Container>
    )
}

export default LoadingScreen